import React, {ReactNode, Fragment} from "react"
import moment from "moment"
import numeral from "numeral"
import {useNavigate} from "react-router-dom"
import ReactTooltip from "react-tooltip"
import {CheckDeposit} from "../../resources/checkDeposits"
import {Resource} from "../../resources/common"
import {OrgName} from "../OrgName/OrgName"
import BodyPortal from "../BodyPortal/BodyPortal"
import {TagsViewerKeyValue} from "../Tags/TagsViewer"
import {getCommonCustomerDetails} from "../Customers/CustomerCardView"
import {checkDepositTooltipValue} from "../CheckDeposits/CheckDepositDetails"
import {getCustomerHref, getCustomerName, resolveCustomers} from "../CheckDeposits/checkDepositUtils"
import {NamesAtUnitCard} from "../NamesAtUnit/NamesAtUnit"
import {UnitUserOnly} from "../../containers/PermissionedUser/PermissionedUser"

interface AssignmentFieldProps {
	label: string
	children: ReactNode
	full?: boolean
}

function AssignmentField({label, children, full = false}: AssignmentFieldProps) {
	return (
		<div className={full ? "check-deposit-assignment-full-field" : "check-deposit-assignment-field"}>
			<span className="check-deposit-assignment-field-label">{label}</span>
			<div className="check-deposit-assignment-field-value">{children}</div>
		</div>
	)
}

interface CheckDepositAssignmentDetailsProps {
	checkDeposit: CheckDeposit
	orgId?: string
	include?: Resource[]
	withCustomerDetails?: boolean
	namesAtUnitCustomerId?: string
}

export function CheckDepositAssignmentDetails({
	checkDeposit,
	orgId,
	include,
	withCustomerDetails,
	namesAtUnitCustomerId,
}: CheckDepositAssignmentDetailsProps) {
	const navigate = useNavigate()

	const {attributes, relationships} = checkDeposit
	const {amount, createdAt: depositDate, settlementDate, description, tags} = attributes
	const number = attributes.checkNumber || undefined

	const customerId = relationships.customer?.data.id
	const customerIds = relationships.customers?.data.map((customer) => customer.id)

	const customers = resolveCustomers(customerId, customerIds, include)
	const primaryCustomer = customers[0]

	const {authorizedUsers} =
		(withCustomerDetails &&
			(primaryCustomer?.type == "individualCustomer" || primaryCustomer?.type == "businessCustomer") &&
			getCommonCustomerDetails(primaryCustomer)) ||
		{}

	const accountId = relationships.account?.data.id
	const account =
		accountId && include
			? include.find((resource) => resource.type == "depositAccount" && resource.id == accountId.toString())
			: undefined
	const accountName = account?.type == "depositAccount" ? account.attributes.name : undefined

	return (
		<div className="check-deposit-assignment-layout">
			<div className="check-deposit-assignment-top-grid">
				{orgId ? (
					<AssignmentField label="Org">
						<OrgName orgId={orgId} element="span" />
					</AssignmentField>
				) : (
					<div aria-hidden="true" />
				)}
				<AssignmentField label="Check Amount">
					{amount != null ? numeral(amount / 100).format("$0,0.00") : "-"}
				</AssignmentField>

				<AssignmentField label="Customer">
					{customers.length > 0
						? customers.map((customer, index) => {
								const customerName = getCustomerName(customer)
								if (!customerName) return null
								const href = getCustomerHref(customer)
								return (
									<Fragment key={customer.id}>
										{index > 0 && ", "}
										<a
											className="link"
											href={href}
											onClick={(e) => {
												e.preventDefault()
												e.stopPropagation()
												navigate(href)
											}}
										>
											{customerName}
										</a>
									</Fragment>
								)
						  })
						: "-"}
				</AssignmentField>
				<AssignmentField label="Check Number">{number || "-"}</AssignmentField>

				<AssignmentField label="Account Name">
					{accountId && accountName ? (
						<a
							className="link"
							href={`/accounts/${accountId}`}
							onClick={(e) => {
								e.preventDefault()
								e.stopPropagation()
								navigate(`/accounts/${accountId}`)
							}}
						>
							{accountName}
						</a>
					) : (
						"-"
					)}
				</AssignmentField>
				<AssignmentField label="Account ID">
					{accountId ? (
						<a
							className="link"
							href={`/accounts/${accountId}`}
							onClick={(e) => {
								e.preventDefault()
								e.stopPropagation()
								navigate(`/accounts/${accountId}`)
							}}
						>
							{accountId}
						</a>
					) : (
						"-"
					)}
				</AssignmentField>
			</div>

			<div className="check-deposit-assignment-bottom-section">
				<AssignmentField label="Authorized Users" full>
					{authorizedUsers || "-"}
				</AssignmentField>
				<AssignmentField label="Names At Unit" full>
					{namesAtUnitCustomerId ? (
						<UnitUserOnly>
							<NamesAtUnitCard customerId={namesAtUnitCustomerId} withoutWrapper emptyFallback="-" />
						</UnitUserOnly>
					) : (
						"-"
					)}
				</AssignmentField>
				<div className="check-deposit-assignment-date-row">
					<AssignmentField label="Deposit Date">
						{depositDate ? moment(depositDate).format("MM/DD/YYYY h:mm A") : "-"}
					</AssignmentField>
					<AssignmentField label="Expected Completion">
						{settlementDate ? (
							<>
								<span
									data-tip="React-tooltip"
									data-for={`check-deposit-assignment-expected-completion-tooltip-${checkDeposit.id}`}
								>
									{moment(settlementDate).format("MM/DD/YYYY")}
								</span>
								<BodyPortal>
									<ReactTooltip
										className="tooltip-info"
										id={`check-deposit-assignment-expected-completion-tooltip-${checkDeposit.id}`}
										disable={!settlementDate}
										effect="solid"
									>
										{checkDepositTooltipValue}
									</ReactTooltip>
								</BodyPortal>
							</>
						) : (
							"-"
						)}
					</AssignmentField>
				</div>
				<AssignmentField label="Description" full>
					{description || "-"}
				</AssignmentField>
				<AssignmentField label="Tags" full>
					{tags ? <TagsViewerKeyValue tags={tags} size="small" /> : "-"}
				</AssignmentField>
			</div>
		</div>
	)
}
